import { useCallback, useState } from 'react';
import logo from './images/New-Dark-bg.svg';
import logoMobile from './images/logo-Priamry-Dark-bg.svg';
import profile from './images/Icon-Profile-Fill.svg';
import helpSvg from './images/HOS-Help.svg';
import helpAnimated from './images/HelpAnimated.png';
import { Dropdown } from 'semantic-ui-react';
import { TopMenu } from '../TopMenu/TopMenu';
import { TopFilter } from '../TopFilter/TopFilter';
import './WebFrameHeader.less';
import { Link } from 'react-router-dom';
import { ROUTE_ROOT } from '../../utils/routes';
import StickyHeader from '../../utils/StickyHeader';
import { useEnvironment } from '@publicisgroupe/p-os-components/build/contexts/EnvironmentContext';
import { useUserContext } from '../../contexts/AuthProvider';
import { Claims } from '../../types/types';
import RequiresPermission from '../../contexts/RequiresPermission';
import { POSIcon } from '@publicisgroupe/p-os-components';
import clsx from 'clsx';
import classNames from 'classnames';
import NavigationFilters from 'components/NavigationFilters';
import { useThemes } from 'components/ThemesProvider/hooks/useThemes';

interface HeaderPros {
  toggleHelpSectionSidebar: () => void;
  toggleUserProfileSettingsSidebar: () => void;
  selectedFiltersChanged: (filters: string[]) => void;
  toggleNotificationSidebar: () => void;
  sidebar?: boolean;
  filters: any[];
  filterNames: string[];
  hasUnreadNotifications: boolean;
}

export const WebFrameHeader = (props: HeaderPros) => {
  const {
    selectedFiltersChanged,
    sidebar,
    filters,
    filterNames,
    toggleHelpSectionSidebar,
    toggleUserProfileSettingsSidebar,
    toggleNotificationSidebar,
    hasUnreadNotifications,
  } = props;
  const { stickyRef, isSticky } = StickyHeader();
  const { settings } = useEnvironment();
  const { userInfo } = useUserContext();
  const themes = useThemes();

  const [showNavigationFilters, setShowNavigationFilters] = useState<boolean>(false);

  const headerLogo = themes?.headerLogo;
  const headerImage = themes?.headerImage
    ? themes.headerImage
    : `${settings.cdnBaseUrl}/webframe/header-bg.jpg`;
  const limitedAccess = userInfo.claims.some((x) => x == Claims.ExternalApprover);

  const onToggleNotificatoinSidebar = () => {
    toggleNotificationSidebar();
  };

  const closeNavigationFilters = useCallback(() => setShowNavigationFilters(false), []);

  const handleSelectedFiltersChanged = useCallback(
    (filters: string[]) => {
      selectedFiltersChanged(filters);
      closeNavigationFilters();
    },
    [closeNavigationFilters, selectedFiltersChanged]
  );

  return (
    <header
      id="webframe-header"
      ref={stickyRef}
      className={sidebar ? 'mobile only' : 'mobile hidden'}
    >
      <div
        className="background-img"
        data-testid="background-img"
        style={{ backgroundImage: `url(${headerImage})` }}
      />
      {limitedAccess && (
        <div
          className="background-img-limited-access"
          style={{ backgroundImage: `url(${headerImage})` }}
        />
      )}
      <div
        className={classNames({
          'webframe-menu-container-limitedAccess': limitedAccess && sidebar,
          'webframe-menu-container': !limitedAccess && !sidebar,
          sticky: isSticky,
        })}
      >
        <div className="logo">
          <Link
            to={ROUTE_ROOT}
            className="mobile hidden logo-largescreen"
            data-testid="logo-largescreen"
          >
            <img src={logo} alt="logo" />
          </Link>
          {headerLogo && (
            <Link
              to={ROUTE_ROOT}
              className="mobile hidden logo-largescreen-theme"
              data-testid="logo-largescreen-theme"
            >
              <img src={headerLogo} alt="theme-logo" />
            </Link>
          )}

          {limitedAccess && sidebar && (
            <img src={logo} alt="sidebar-logo" data-testid="logo-sidebar" />
          )}

          {!limitedAccess && (
            <Link to={ROUTE_ROOT} className="mobile only logo-mobile" data-testid="logo-mobile">
              <img src={logoMobile} alt="mobile-logo" />
            </Link>
          )}
          {headerLogo && !limitedAccess && (
            <Link
              to={ROUTE_ROOT}
              className="mobile only logo-mobile-theme"
              data-testid="logo-mobile-theme"
            >
              <img src={headerLogo} alt="mobile-theme-logo" />
            </Link>
          )}
        </div>
        {!isSticky && <TopMenu />}

        {!limitedAccess && (
          <div id="top-filter-container" className="mobile hidden">
            <TopFilter
              filters={filters}
              filterNames={filterNames}
              showFiltersBigMenu={showNavigationFilters}
              selectedFiltersChanged={selectedFiltersChanged}
              onToggleFiltersBigMenu={(showFilters: boolean) =>
                setShowNavigationFilters(showFilters)
              }
            />
            <NavigationFilters
              filters={filters}
              filterNames={filterNames}
              onFiltersModalClose={closeNavigationFilters}
              showFiltersModal={showNavigationFilters}
              selectedFiltersChanged={handleSelectedFiltersChanged}
            />
          </div>
        )}
        <div className={clsx('user-actions', { 'limited-access': limitedAccess })}>
          <div className={clsx('action profile', { 'limited-access': limitedAccess })}>
            <Dropdown
              direction="left"
              icon={
                <img
                  src={profile}
                  alt="Profile"
                  onClick={toggleUserProfileSettingsSidebar}
                  onKeyDown={toggleUserProfileSettingsSidebar}
                  role="presentation"
                />
              }
            />
          </div>
          <RequiresPermission permission="Webframe.HelpSection">
            <div
              onClick={toggleHelpSectionSidebar}
              className={clsx('action help', { 'limited-access': limitedAccess })}
            >
              <img src={helpSvg} alt="Profile" width="37px" height="37px" />
              <img src={helpAnimated} className="animated" alt="Profile" />
            </div>
          </RequiresPermission>
          <RequiresPermission permission="Webframe.NotificationArea">
            <div
              className={clsx(
                'action notification',
                { 'limited-access': limitedAccess },
                { active: hasUnreadNotifications }
              )}
              onClick={onToggleNotificatoinSidebar}
            >
              <POSIcon icon={'notification'} />
            </div>
          </RequiresPermission>
        </div>
      </div>
    </header>
  );
};
